<template>
  <div class="space-y-4">
    <h2>{{ t('infringementFees') }}</h2>
    <div class="space-y-3 rounded-md bg-white p-4">
      <div
        v-for="(field, index) in fields"
        :key="field.key"
        class="space-y-2 border-b pb-3 last:border-b-0 last:pb-0"
      >
        <div class="grid grid-cols-[1fr_2fr_auto] items-end gap-3">
          <InputField
            :name="`infringementFees[${index}].amount`"
            :label="t('amount')"
            type="number"
            :min="0"
            :max-fraction-digits="2"
            adornment="€"
          />
          <div class="flex items-end gap-2">
            <template
              v-if="!isSingleDefaultRow(index) && isCountrySelectVisible(String(field.key))"
            >
              <SearchableSelect
                :model-value="getSelectedCountry(String(field.key))"
                class="flex-1"
                :label="t('countries')"
                :options="getCountryOptions()"
                :placeholder="t('chooseCountry')"
                :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
                @update:model-value="selectedCountries[String(field.key)] = String($event)"
              />
              <CVButton
                variant="secondary"
                size="sm"
                :disabled="!getSelectedCountry(String(field.key))"
                @click.prevent="addCountry(index)"
              >
                {{ t('add') }}
              </CVButton>
              <CVButton
                variant="ghost"
                size="sm"
                @click.prevent="hideCountrySelect(String(field.key))"
              >
                {{ t('cancel') }}
              </CVButton>
            </template>
            <CVButton
              v-else-if="!isSingleDefaultRow(index)"
              variant="ghost"
              size="sm"
              @click.prevent="showCountrySelect(String(field.key))"
            >
              + {{ t('addCountry') }}
            </CVButton>
          </div>
          <button
            class="px-2 py-3"
            :class="{
              'text-bittersweet-600': !isSingleDefaultRow(index),
              'text-obsidian-400': isSingleDefaultRow(index),
            }"
            :disabled="isSingleDefaultRow(index)"
            @click.prevent="removeFee(index)"
          >
            <XIcon class="size-4" />
          </button>
        </div>
        <div v-if="(field.value.countries ?? []).length" class="flex flex-wrap gap-2">
          <div
            v-for="country in field.value.countries ?? []"
            :key="country"
            class="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-white"
          >
            <span>{{ getCountryLabel(country) }}</span>
            <button
              class="text-white/80 hover:text-white"
              @click.prevent="removeCountry(index, country)"
            >
              <XIcon class="size-3" />
            </button>
          </div>
        </div>
        <div
          v-else
          class="inline-flex items-center rounded-full bg-obsidian-100 px-2.5 py-1 text-xs font-medium text-obsidian-700"
        >
          {{ t('defaultFeeBadge') }}
        </div>
      </div>
      <div class="flex justify-center">
        <CVButton variant="ghost" @click.prevent="addFee">
          {{ t('addFee') }}
        </CVButton>
      </div>
      <InputHint :hint="infringementFeesError" :is-error="!!infringementFeesError" />
    </div>
  </div>
</template>

<script setup lang="ts">
import InputField from '@/components/InputField.vue';
import InputHint from '@/components/InputHint.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { getCountries } from '@/utils/countries';
import { useFieldArray, useFieldError, useFormContext } from 'vee-validate';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { XIcon } from 'lucide-vue-next';
import SearchableSelect from '@/components/SearchableSelect.vue';
import { Alert } from '@/services/alert.service';

type InfringementFeeFormEntry = {
  amount: number | null;
  countries: string[] | null;
};

const { t, locale } = useI18n();
const { fields, push } = useFieldArray<InfringementFeeFormEntry>('infringementFees');
const { setFieldValue, values } = useFormContext<{
  infringementFees: InfringementFeeFormEntry[];
}>();
const infringementFeesError = useFieldError('infringementFees');
const visibleCountrySelectRows = ref<Record<string, true>>({});
const selectedCountries = ref<Record<string, string | undefined>>({});

const allCountries = computed(() => getCountries(locale.value));

const getCountryOptions = () => {
  const selectedCountries = new Set(fields.value.flatMap((field) => field.value.countries ?? []));
  return allCountries.value.filter((country) => !selectedCountries.has(country.value));
};

const getCountryLabel = (countryCode: string) =>
  allCountries.value.find((country) => country.value === countryCode)?.label ?? countryCode;

const getSelectedCountry = (rowKey: string) => selectedCountries.value[rowKey];
const isCountrySelectVisible = (rowKey: string) => !!visibleCountrySelectRows.value[rowKey];

const showCountrySelect = (rowKey: string) => {
  visibleCountrySelectRows.value[rowKey] = true;
  selectedCountries.value[rowKey] = undefined;
};

const hideCountrySelect = (rowKey: string) => {
  delete visibleCountrySelectRows.value[rowKey];
  delete selectedCountries.value[rowKey];
};

const isDefaultRow = (field: InfringementFeeFormEntry) => {
  const countries = field.countries;
  return countries === null || countries.length === 0;
};

const isSingleDefaultRow = (index: number) =>
  fields.value.filter((field) => isDefaultRow(field.value)).length === 1 &&
  isDefaultRow(fields.value[index].value);

const removeFee = async (index: number) => {
  const { isDenied } = await Alert.fire({
    text: t('confirmDeleteFee'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  const nextFees = values.infringementFees.filter((_, currentIndex) => currentIndex !== index);
  setFieldValue('infringementFees', nextFees);
};

const addCountry = (index: number) => {
  const rowKey = String(fields.value[index].key);
  const selectedCountry = getSelectedCountry(rowKey);
  if (!selectedCountry) {
    return;
  }
  const currentCountries = fields.value[index]?.value.countries ?? [];
  if (currentCountries.includes(selectedCountry)) {
    selectedCountries.value[rowKey] = undefined;
    return;
  }
  setFieldValue(`infringementFees[${index}].countries`, [
    ...currentCountries,
    selectedCountry,
  ] as unknown as never);
  selectedCountries.value[rowKey] = undefined;
  hideCountrySelect(rowKey);
};

const removeCountry = (index: number, countryToRemove: string) => {
  const currentCountries = fields.value[index]?.value.countries ?? [];
  setFieldValue(
    `infringementFees[${index}].countries`,
    currentCountries.filter((country) => country !== countryToRemove) as unknown as never,
  );
};

const addFee = () => {
  push({
    amount: null,
    countries: [],
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "infringementFees": "Infringement fees",
    "amount": "Fee amount",
    "countries": "Countries",
    "chooseCountry": "Choose country",
    "addCountry": "Add country",
    "cancel": "Cancel",
    "delete": "Delete",
    "confirmDeleteFee": "Do you really want to delete this entry?",
    "countriesHint": "Leave empty for default (all countries not used in other rows).",
    "defaultFeeBadge": "Default for all remaining countries",
    "addFee": "+ Add fee"
  },
  "de": {
    "infringementFees": "Ordnungswidrigkeitsgebühren",
    "amount": "Gebührenbetrag",
    "countries": "Länder",
    "chooseCountry": "Land auswählen",
    "addCountry": "Land hinzufügen",
    "cancel": "Abbrechen",
    "delete": "Löschen",
    "confirmDeleteFee": "Möchtest du diesen Eintrag wirklich löschen?",
    "countriesHint": "Leer lassen für Standard (alle Länder, die in keiner anderen Zeile sind).",
    "defaultFeeBadge": "Standard für alle übrigen Länder",
    "addFee": "+ Gebühr hinzufügen"
  }
}
</i18n>
